import { createApi, fetchBaseQuery } from "@reduxjs/toolkit/query/react";

export const commentsApi = createApi({
  reducerPath: "commentsApi",
  baseQuery: fetchBaseQuery({
    baseUrl: import.meta.env.VITE_API + "users/comment/",
    prepareHeaders: (headers, { getState }) => {
      const token = getState().userSlice?.token;
      if (token) {
        headers.set("Authorization", `Bearer ${token}`);
      }
      return headers;
    },
  }),
  tagTypes: [
    "Comments",
    "BlogComment",
    "OrderComment",
    "ProviderComment",
    "CustomerComment",
    "SiteComment",
  ],
  endpoints: (builder) => ({
    // ========== EXISTING ENDPOINTS (kept exactly as you had) ==========
    getAllProviderComment: builder.query({
      query: ({ providerId, page, limit }) => ({
        url: `provider/${providerId}??page=${page}&limit=${limit}`,
        method: "GET",
      }),
      providesTags: (result, error, { providerId }) =>
        result?.data
          ? [
              ...result.data.map(({ _id }) => ({
                type: "ProviderComment",
                id: _id,
              })),
              { type: "ProviderComment", id: providerId },
            ]
          : [{ type: "ProviderComment", id: providerId }],
      // transformResponse: (response) => response?.data || response,
    }),
    getAllProviderProfileComment: builder.query({
      query: ({ providerId, page, limit }) => ({
        url: `profile/${providerId}??page=${page}&limit=${limit}`,
        method: "GET",
      }),
    }),
    getAllCustomerComment: builder.query({
      query: ({ customerId, page, limit }) => ({
        url: `customer/${customerId}?page=${page}&limit=${limit}`,
        method: "GET",
      }),
      providesTags: (result, error, { customerId }) =>
        result?.data
          ? [
              ...result.data.map(({ _id }) => ({
                type: "CustomerComment",
                id: _id,
              })),
              { type: "CustomerComment", id: customerId },
            ]
          : [{ type: "CustomerComment", id: customerId }],
      // transformResponse: (response) => response?.data || response,
    }),

    getAllSiteComment: builder.query({
      query: () => ({
        url: `site`,
        method: "GET",
      }),
      providesTags: (result) =>
        result?.data
          ? [
              ...result.data.map(({ _id }) => ({
                type: "SiteComment",
                id: _id,
              })),
              { type: "SiteComment", id: "LIST" },
            ]
          : [{ type: "SiteComment", id: "LIST" }],
      transformResponse: (response) => response?.data || response,
    }),

    getAllOrderComment: builder.query({
      query: ({ orderId }) => ({
        url: `order/${orderId}`, // 🔧 FIXED: was provider/${orderId}
        method: "GET",
      }),
      providesTags: (result, error, orderId) =>
        result?.data
          ? [
              ...result.data.map(({ _id }) => ({
                type: "OrderComment",
                id: _id,
              })),
              { type: "OrderComment", id: orderId },
            ]
          : [{ type: "OrderComment", id: orderId }],
      transformResponse: (response) => response?.data || response,
    }),

    getAllBlogComment: builder.query({
      query: (blogId) => ({
        url: `blog/${blogId}`, // 🔧 FIXED: was provider/${blogId}
        method: "GET",
      }),
      providesTags: (result, error, blogId) =>
        result?.data
          ? [
              ...result.data.map(({ _id }) => ({
                type: "BlogComment",
                id: _id,
              })),
              { type: "BlogComment", id: blogId },
            ]
          : [{ type: "BlogComment", id: blogId }],
      transformResponse: (response) => response?.data || response,
    }),

    createComment: builder.mutation({
      query: ({ commentType, relativeId, text, rate, from } = {}) => {
        const payload = {};
        if (commentType != null) payload.commentType = commentType;
        if (relativeId != null) payload.relativeId = relativeId;
        if (text != null) payload.text = text;

        if (rate != null) payload.rate = rate;
        if (from.user) payload.from = from;
        return {
          url: "",
          method: "POST",
          body: payload,
        };
      },
      invalidatesTags: (result, error, { commentType, relativeId }) => {
        const tags = [];
        if (commentType === "blog")
          tags.push({ type: "BlogComment", id: relativeId });
        if (commentType === "site")
          tags.push({ type: "SiteComment", id: "LIST" });
        if (commentType === "user")
          tags.push({ type: "OrderComment", id: relativeId });
        if (commentType === "user" && result?.data?.to?.user) {
          tags.push({ type: "ProviderComment", id: result.data.to.user });
          tags.push({ type: "CustomerComment", id: result.data.to.user });
        }
        return tags;
      },
    }),

    // ========== NEW ENDPOINTS (extending without breaking existing) ==========
    updateComment: builder.mutation({
      query: ({ id, text, rate }) => ({
        url: `${id}`,
        method: "PATCH",
        body: { text, rate },
      }),
      invalidatesTags: (result, error, { id }) => [
        { type: "Comments", id },
        { type: "BlogComment", id },
        { type: "OrderComment", id },
        { type: "SiteComment", id },
        { type: "ProviderComment", id },
        { type: "CustomerComment", id },
      ],
    }),

    acceptComment: builder.mutation({
      query: (id) => ({
        url: `${id}`,
        method: "POST",
      }),
      invalidatesTags: (result, error, id) => [
        { type: "Comments", id },
        { type: "BlogComment", id },
        { type: "OrderComment", id },
        { type: "SiteComment", id },
        { type: "ProviderComment", id },
        { type: "CustomerComment", id },
      ],
    }),

    rejectComment: builder.mutation({
      query: (id) => ({
        url: `${id}`,
        method: "DELETE",
      }),
      invalidatesTags: (result, error, id) => [
        { type: "Comments", id },
        { type: "BlogComment", id },
        { type: "OrderComment", id },
        { type: "SiteComment", id },
        { type: "ProviderComment", id },
        { type: "CustomerComment", id },
      ],
    }),
  }),
});

// ========== EXPORT ALL HOOKS ==========
// Existing hooks (unchanged)
export const {
  useGetAllProviderCommentQuery,
  useGetAllCustomerCommentQuery,
  useGetAllOrderCommentQuery,
  useGetAllBlogCommentQuery,
  useGetAllSiteCommentQuery,
  useCreateCommentMutation,
  useGetAllProviderProfileCommentQuery,
} = commentsApi;

// Additional hooks (new)
export const {
  useUpdateCommentMutation,
  useAcceptCommentMutation,
  useRejectCommentMutation,
} = commentsApi;
